import { CastApiErrorCode } from '../types/errors'
import { Cast } from './cast'
import CastAPI, { CastParamsOptional } from './CastAPI'
import { CollectionNames } from '@/modules/database/utils/collectionNames'
import firebase from 'firebase/app'
import Convertable from '@/classes/Convertable'
import { generateRandomString } from '@/modules/common/utils'
import { CastType } from '@/types/casts'
import { Result } from '@/modules/errorHandling/results'
import { CastStationSwitcher } from './CastStationSwitcher'
import { SwitcherCastParamsSaveCast } from './SwitcherCastAPI'
import { SourceStream, StreamType } from '@/types/streams'
import { getAudioTracksForCount } from '@/modules/audiomultitrack/utils/audioTracks'
import { KeyDict } from '@/types'
import { DEFAULT_TRACKS_SWITCHER } from '@/modules/audiomultitrack/types'
import { Scene } from '@/types/scenes'
import { SceneSourceType } from '@/modules/scenes/types'
import { CastPlain } from './CastPlain'
import { isSourceStreamCef } from '@/modules/streams/types'
import { isFanRoomCast } from '../utils/isCastType'
import { CastFanRoom } from './CastFanRoom'

export default class StationSwitcherCastAPI extends CastAPI {
  private _castDocRef: firebase.firestore.DocumentReference<firebase.firestore.DocumentData>|null = null
  /*eslint-disable max-lines-per-function*/
  override async createCast (castParams: SwitcherCastParamsSaveCast, currentUserId: string,
    _optionalParams?: CastParamsOptional): Promise<Result<CastStationSwitcher, CastApiErrorCode>>
  {
    this._teamId = castParams.team_id
    const version = await this.getDefaultVersion()
    try {
      this._castDocRef = this._db.collection(CollectionNames.CASTS).doc()
      const result = await this._db.runTransaction(async (transaction: firebase.firestore.Transaction) => {
        this._transaction = transaction
        if (this._castDocRef === null) {
          return Result.fail(CastApiErrorCode.CREATE_CAST, 'Could not create cast document')
        }
        const castId = this._castDocRef.id
        const fanRooms = await this.getFanRooms(castId, castParams)
        const eventId = this.createBlankEvent({ ...castParams, castId, castType: CastType.StationSwitcher })
        const sourceStreams = this.setupSourceStreams(castParams)
        for (const sourceStream of Object.values(sourceStreams)) {
          if (sourceStream.type !== StreamType.Playlist) continue
          sourceStream.active = this.hasPlaylistSource(castParams.scenes)
        }
        const token = generateRandomString(24)
        const cast = this.saveCast(castId, eventId, castParams, sourceStreams, token, version, currentUserId)
        this.setLastEventTimestamp()
        this.createPointerData(castId, castParams.name, token, version)
        await this.updateFanRooms(fanRooms)
        return cast
      })
      if (result.isSuccess) return result
      else return Result.fail(CastApiErrorCode.CREATE_CAST, 'Error creating cast')
    } catch (error) {
      console.error(`Error creating cast ${ error }`)
    }
    return Result.fail(CastApiErrorCode.CREATE_CAST, 'Error creating cast')
  }

  async updateCast (castParams: Cast & { id: string, team_id: string }): Promise<Result<Cast, CastApiErrorCode>> {
    this._teamId = castParams.team_id ?? null
    try {
      const cast = await this.updateExistingCast(castParams)
      if (cast.isSuccess) {
        return Result.success(new CastStationSwitcher(cast.value))
      }
    } catch (error) {
      console.error('Error updating cast', error)
    }
    return Result.fail(CastApiErrorCode.UPDATE_CAST, 'Error updating cast')
  }

  private async saveCast (castId: string, eventId: string, castParams: SwitcherCastParamsSaveCast,
                          sourceStreams: KeyDict<SourceStream>, token: string, version: string,
                          currentUserId: string): Promise<Result<CastStationSwitcher, CastApiErrorCode>> {
    const chatDocRef = this._db.collection(CollectionNames.CHATS).doc()
    this._transaction?.set(chatDocRef, { team_id: castParams.team_id, messages: [] })
    const cast = new CastStationSwitcher({
      ...castParams, id: castId, event_id: eventId,
      castType: CastType.StationSwitcher,
      token, online_sessions: {},
      chat_id: chatDocRef.id,
      widgets: {},
      source_streams: sourceStreams,
      version: version,
      creator_id: currentUserId,
      anonymous_caster_rules: this.getAnonymousCasterRules(),
      record_isos: false,
      audioTracks: getAudioTracksForCount(DEFAULT_TRACKS_SWITCHER),
      hasLiveClipping: true
    })
    if (this._castDocRef === null) {
      return Result.fail(CastApiErrorCode.CREATE_CAST, 'Could not create cast document')
    }
    this._transaction?.set(this._castDocRef, Convertable.toObject({ ...cast }))
    const widgets = Convertable.toObject(await this.setupWidgets({ ...castParams, id: cast.id }))
    this._transaction?.set(this._castDocRef, { id: cast.id, widgets }, { merge: true })
    return Result.success(cast)
  }

  protected override getAnonymousCasterRules () {
    return {
      max_anonymous_casters: 0,
      max_total_casters: 0,
      max_anonymous_accounts: 50
    }
  }

  protected override setupSourceStreams (castParams: Partial<CastPlain>): KeyDict<SourceStream> {
    const sourceStreams = super.setupSourceStreams(castParams)
    for (const [key, stream] of Object.entries(castParams.source_streams ?? {})) {
      if (isSourceStreamCef(stream)) {
        sourceStreams[key] = stream
      }
    }
    return sourceStreams
  }

  private async getFanRooms (castId: string, castParams: Partial<CastPlain>): Promise<KeyDict<CastFanRoom>> {
    if (!this._transaction) return Promise.reject('Failed loading fan room. Transaction unavailable.')
    const fanRooms: KeyDict<CastFanRoom> = {}
    for (const stream of Object.values(castParams.source_streams ?? {})) {
      if (!isSourceStreamCef(stream)) continue
      const fanRoomCastId = stream.castId
      const castDocRef = this._db.collection(CollectionNames.CASTS).doc(fanRoomCastId)
      const fanRoomCastDoc = await this._transaction.get(castDocRef)
      if (!fanRoomCastDoc.exists) {
        console.error(`Skipping loading linked fan room. Fan room ${fanRoomCastId} in cast ${castId} not found`)
        continue
      }
      const result = Cast.load(fanRoomCastDoc.data())
      if (result.isFailure) {
        result.logIfError(`Skipping loading linked fan room. Error converting cast doc : ${fanRoomCastDoc.id}.`)
        continue
      }
      const fanRoomCast = result.value
      if (!isFanRoomCast(fanRoomCast)) {
        console.error(`Skipping loading linked fan room. Linked cast ${fanRoomCast.id} is not a fan room`)
        continue
      }
      fanRooms[fanRoomCast.id] = fanRoomCast
    }
    return fanRooms
  }

  private async updateFanRooms (fanRooms: KeyDict<CastFanRoom>): Promise<void> {
    if (!this._transaction) return Promise.reject('Cannot increment fan room linked count. Transaction unavailable.')
    for (const [fanRoomCastId, fanRoomCast] of Object.entries(fanRooms)) {
      const castDocRef = this._db.collection(CollectionNames.CASTS).doc(fanRoomCastId)
      const linkedCount = fanRoomCast.activeState.linked
      this._transaction?.set(castDocRef, { activeState: { linked: linkedCount+1 } }, { merge: true })
    }
  }

  private hasPlaylistSource (scenes: KeyDict<Scene>) {
    return Object.values(scenes).some((scene) => scene.sourceType === SceneSourceType.Playlist)
  }
}
